import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import traversal from "web/script/dom/traversal";
import browser from "web/script/utils/browser";
import { events as calloutEvents } from "web/script/components/app-header-callout";


const name = "app-header";

const selectors = {
    PRE_HEADER_BAR: ".app-pre-header-bar",
    PRE_HEADER_BAR_BURGER: ".app-pre-header-bar__burger-inner",
    GENDER: `.${name}-bar__gender`,
    SEARCH_INPUT_CLASS_NAME: `${name}-search__input`,
};

const states = {
    GENDER_ACTIVE: `${name}-bar__gender--active`,
    GENDER_SELECTED: `${name}-bar__gender--selected`,
};

export default baustein.register(name, {
    /**
     * Most of the methods query the Menu component.
     * Cache it so we don't need to query it every time.
     * @type {Object | null}
     */
    menu: null,

    /**
     * Whether there are any open callouts within the header.
     * Consumed by _handleOpenCallouts.
     * @see _handleOpenCallouts
     * @type {number}
     */
    openCallouts: 0,

    // We want to save the current app header position to be restored later
    // when users exit the search input
    translateMagnitude: 0,

    init() {
        this.menu = this.findComponent("app-header-menu");
    },

    setupEvents(add) {
        const { menu } = this;
        if (menu) {
            const patientMenuToggling = _.throttle(this._toggleMenu.bind(this), menu.animationTime);
            const patientGlobalClick = _.throttle(
                this._onGlobalClick.bind(this),
                menu.animationTime
            );

            add("click", selectors.GENDER, patientMenuToggling);

            add("appHeaderMenuClosed", this._onClosedMenu);

            [calloutEvents.OPENED, calloutEvents.CLOSED].forEach((e) =>
                add(e, this._handleOpenCallouts)
            );

            if (!menu.isNarrowMenu()) {
                this.setGlobalHandler("click", patientGlobalClick);
            }
        }

        add("searchFocused", this.onSearchFocused);
        add("searchBlurred", this.onSearchBlurred);
    },

    /**
     * Toggle the Header menu. Communicates with 'components/app-header-menu'.
     * When the menu is open, it adds an active state to the gender CTA.
     *
     * @see components/app-header-menu'
     * @param {Object} event
     * @param {HTMLElement} targetEl
     * @returns {undefined}
     */
    _toggleMenu(event, targetEl) {
        const { menu } = this;
        const switchGender = this._getActiveGenderCTA() !== targetEl && menu.isOpen;
        const targetGender = targetEl.getAttribute("data-target-menu") || "women";

        if (this._isGenderCTA(targetEl)) {
            event.preventDefault();
        }

        requestAnimationFrame(() => {
            menu.toggle(targetGender, switchGender);

            if (menu.isOpen || switchGender) {
                this._cleanGenderCTAs();
                targetEl.classList.add(states.GENDER_ACTIVE);
            }
        });

        const appHeaderSearch = this.findComponent("app-header-search");
        if (appHeaderSearch) {
            appHeaderSearch.onMenuGenderChange(targetGender);
        }
    },

    /**
     * Generic action of closing the menu and removing any active/selected states
     * from the Gender CTAs. If we're closing the narrow menu, re-adjust the Header element
     * translateY position.
     *
     * @see _getActiveGenderCTA, _cleanGenderCTAs _onMenuOpen
     * @returns {undefined}
     */
    _onClosedMenu() {
        const activeGenderEl = this._getActiveGenderCTA();

        this._cleanGenderCTAs();

        if (activeGenderEl) {
            activeGenderEl.classList.add(states.GENDER_SELECTED);
        }
    },

    /**
     * @param {Object} event
     * @returns {undefined}
     */
    _onGlobalClick(event) {
        const { target } = event;
        const { menu } = this;

        // `target.parentElement` We need to add link content in a span to accommodate the chevron
        // in `app-header-bar-simplified.jinja`, then check <a> for correct active classes
        if (
            !menu.isOpen ||
            this._isGenderCTA(target.parentElement) ||
            traversal.contains(menu.el, target)
        ) {
            return;
        }

        menu.toggle();
    },

    /**
     * Removes any active state from the Gender CTAs.
     *
     * @returns {undefined}
     */
    _cleanGenderCTAs() {
        [...this.el.querySelectorAll(selectors.GENDER)].forEach((el) => {
            el.classList.remove(states.GENDER_ACTIVE);
            el.classList.remove(states.GENDER_SELECTED);
        });
    },

    /**
     * Gets the current active Gender CTA.
     *
     * @returns {HTMLElement | undefined}
     */
    _getActiveGenderCTA() {
        return [...this.el.querySelectorAll(selectors.GENDER)].filter((el) =>
            el.classList.contains(states.GENDER_ACTIVE)
        )[0];
    },

    /**
     * Asserts whether a supplied element is a Gender CTA or not.
     *
     * @returns {boolean}
     */
    _isGenderCTA(el) {
        return el.classList.contains(selectors.GENDER.substr(1));
    },

    /**
     * Mimics implementation of `_setTransform` from components/desktop-header.
     * A helper consumed by _onScroll.
     *
     * @see _onScroll
     * @see components/desktop-header _onScroll
     * @param {number} translateY How much to offset on the Y axis the associated component element.
     * @returns {undefined}
     */
    _setTransform(translateY) {
        this.el.style.transform = `translateY(${translateY}px)`;
    },

    /**
     * Track interactions with trending searches [WEB-2487].
     *
     * @param {Object} event Baustein's wrapper around DOM native event object.
     * @param {HTMLElement} target The HTML element that fired the event.
     * @returns {undefined}
     */
    _trackTrendingSearch(event, target) {
        analytics.event(
            "trending_search",
            "tap_trending_search",
            `term: ${target.textContent.trim()}`
        );
    },

    /**
     * Manages when to query or not for Lyst Matte depending on the number of callouts open
     * within the header.
     *
     * @see components/app-header-callout
     * @param {Object} event Baustein's wrapper around DOM native event object.
     * @return {undefined}
     */
    _handleOpenCallouts({ type }) {
        if (type === calloutEvents.OPENED) {
            if (this.openCallouts === 0) {
                this.emit(calloutEvents.OPEN_LYST_MATTE);
            }

            ++this.openCallouts;
        }

        if (type === calloutEvents.CLOSED) {
            if (this.openCallouts === 1) {
                this.emit(calloutEvents.CLOSE_LYST_MATTE);
            }

            --this.openCallouts;
        }
    },

    onSearchFocused() {
        var searchFocusedOffset = 85;
        const preHeaderEl = this.el.querySelector(selectors.PRE_HEADER_BAR_BURGER);
        if (preHeaderEl) {
            searchFocusedOffset = 0;
        }
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            requestAnimationFrame(() => this._setTransform(-searchFocusedOffset));
            // Notifies mobile-feed-toolbar how much to shift itself by so that it
            // is still positioned right after app header
            this.emit("appHeaderUpdatedOffset", { searchFocusedOffset });
        }
    },

    onSearchBlurred() {
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            // Restore the previous app header position
            requestAnimationFrame(() => this._setTransform(this.translateMagnitude));
            // Notifies mobile-feed-toolbar about the shift in position
            this.emit("appHeaderUpdatedOffset", {
                offset: -this.translateMagnitude,
            });
        }
    },
});
